import { useEffect, useState, Suspense } from 'react'
import { BrowserRouter as Router } from 'react-router-dom'
import classNames from 'classnames'
import i18n from 'i18next'

import AppRoutes from './AppRoutes'

import { ModalProvider } from 'context/ModalContext'
import { WebSocketProvider } from 'context/WebSocketProvider'
import { getData } from 'helpers/api'

import Toastify from 'components/Toastify'
import Loader from 'components/Loader'
import ScrollToTop from 'modules/ScrollToTop'
import Update from 'modules/Update'

import style from './index.module.scss'

import { useSettingsStore } from 'stores/settingsStore'
import { useFavoritesStore } from 'stores/favoritesStore'
import { useCategoriesStore } from 'stores/categoriesStore'
import { useAuthStore } from 'stores/authStore'

const App = () => {
  const { setAuth, isAuth } = useAuthStore()
  const { setSettings } = useSettingsStore()
  const { setFavorites } = useFavoritesStore()
  const { setCategories } = useCategoriesStore()

  const [loading, setLoading] = useState(true)

  const loadConfig = async () => {
    const response = await fetch('/json/config.json')
    const config = await response.json()
    localStorage.setItem('config', JSON.stringify(config.hostnames))
  }

  const initStores = async () => {
    let favorites = {}

    const [settings, auth, categories] = await Promise.all([
      setSettings(),
      setAuth(),
      setCategories(),
    ])

    if (auth?.id) {
      favorites = await setFavorites()
    }

    return { settings, auth, categories, favorites }
  }

  useEffect(() => {
    const initApp = async () => {
      try {
        await loadConfig()
        const { settings, auth, categories } = await initStores()

        if (settings && auth && categories) {
          const storedLanguage = JSON.parse(sessionStorage.getItem('language'))
          const defaultLanguage = auth?.language?.code
          i18n.changeLanguage(storedLanguage || defaultLanguage || 'ukr')
        }
      } catch (err) {
        console.error('Init error:', err)
      } finally {
        setLoading(false)
      }
    }

    initApp()
  }, [])

  useEffect(() => {
    const ping = async () => {
      try {
        await getData('ping/')
      } catch (e) {
        console.log('Ping failed')
      }
    }

    const interval = setInterval(ping, 30000)

    return () => clearInterval(interval)
  }, [])

  if (loading) return <Loader type="fullscreen" />

  return (
    <Router>
      <ScrollToTop />
      <ModalProvider>
        <div
          className={
            classNames(
              style.block,
              isAuth() ? style.auth : style.default
            )
          }
        >
          <WebSocketProvider>
            <Suspense fallback={<Loader type={'fullscreen'} />}>
              <AppRoutes />
              <Update />
            </Suspense>
          </WebSocketProvider>
        </div>
      </ModalProvider>
      <Toastify />
    </Router>
  )
}

export default App
